import {
    useEffect,
    useRef,
    useState,
    VoidFunctionComponent,
    useMemo,
    MutableRefObject,
    useCallback,
    useLayoutEffect,
    ComponentProps,
} from 'react';
import { useMount } from 'react-use';
import { ethers } from 'ethers';
import { useFrame } from '@react-three/fiber';
import { useProvider } from 'wagmi';
import { Environment, Text } from '@react-three/drei';
import { DirectionalLight, Group, MathUtils, MeshBasicMaterial, Object3D } from 'three';
import { OnlyBot, OnlyBotAnchor } from '@animavirtuality/onlybots-core';
import { BotInstancedMesh } from '@/lib/types';
import { Screen } from '@/components/Screen';
import { useResponsive } from '@/hooks/useResponsive';
import { garamondNarrowLight } from '@/lib/fonts';
import { PlaystationButton } from '@/components/PlaystationButton';
import { eggData } from '@/lib/eggData';
import { formatVoxelMaterialsFromBot } from '@/lib/voxelBot';
import { applyMaterial, DEFAULT_COLOR } from '@/lib/materials';
import { useLerpedMouse } from '@/hooks/useLerpedMouse';
import { useVoxelExplosion } from '@/hooks/useVoxelExplosion';
import { MAX_VOXEL_COUNT, tokenContractAddress } from '@/lib/globals';
import { clampedLerp } from '@/lib/mathUtils';
import { useDeviceOrientation } from '@/hooks/useDeviceOrientation';
import { SelectBloom } from '@/components/SelectBloom';
import { bgBlack } from '@/lib/colors';
import mintingSound from '@/sounds/ob-music-minting.mp3';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';
import switchBot1Sound from '@/sounds/ob-sfx-title-switch-bot-1.mp3';
import { useRouterContext } from '@/context/RouterContext';
import OnlyBotsToken from '@/contracts/OnlyBotsToken.json';
import { convertShaderToPreset } from '@/lib/presets';

const TRANSFORMATION_STORAGE = new Object3D();
const EXPLODE_TIME = 0.6;
const SWIRL_TIME = 0.5;
const CONSOLIDATE_TIME = 1.0;
const NAME_FADE_IN_TIME = 1.5;
const VOXEL_CLOUD_SPEED = 0.009;
const VOXEL_CLOUD_SIZE = 0.6;
const TIME_TO_CENTER_EGG = 1;
const EGG_SCALE = 0.15;
const BOT_SCALE = 0.3;
const LIGHT_INTENSITY = 2;
const REDIRECT_TO_EDITION_DURATION = 7;
const REVEAL_AFTER_DURATION = 7;

type TextProps = ComponentProps<typeof Text>;
const scaleAnchor = (anchor: OnlyBotAnchor): OnlyBotAnchor => ({
    x: BOT_SCALE * anchor.x,
    y: BOT_SCALE * anchor.y,
    z: BOT_SCALE * anchor.z,
});

interface Props {
    cancel: () => void;
}

export const Reveal: VoidFunctionComponent<Props> = ({ cancel }) => {
    const [revealClicked, setRevealClicked] = useState<boolean>(false);
    const [revealBot, setRevealBot] = useState<boolean>(false);
    const { tokenId, navigate } = useRouterContext();
    const light1 = useRef<DirectionalLight>(new DirectionalLight());
    const light2 = useRef<DirectionalLight>(new DirectionalLight());
    const provider = useProvider();
    const { scale, responsiveValue } = useResponsive();
    const [bot, setBot] = useState<OnlyBot | null>(null);
    const instance0 = useRef<BotInstancedMesh>(null);
    const instance1 = useRef<BotInstancedMesh>(null);
    const instance2 = useRef<BotInstancedMesh>(null);
    const instance3 = useRef<BotInstancedMesh>(null);
    const instancedMeshRefs = useMemo(() => [instance0, instance1, instance2, instance3], []);
    const { orientation } = useDeviceOrientation();
    const [playMintingSound, { stop: stopMintingSound }] = useOnlybotsSound(mintingSound);
    const [playMintedSound] = useOnlybotsSound(switchBot1Sound);
    const currentBot = bot ?? eggData;
    const materials = useMemo(() => formatVoxelMaterialsFromBot(currentBot), [currentBot]);
    const mouse = useLerpedMouse();
    const groupRef = useRef<Group>(new Group());
    const nameRef = useRef<TextProps>();
    const { explode, consolidate } = useVoxelExplosion(
        instancedMeshRefs,
        materials,
        groupRef,
        EXPLODE_TIME,
        SWIRL_TIME,
        CONSOLIDATE_TIME,
        VOXEL_CLOUD_SPEED,
        VOXEL_CLOUD_SIZE
    );
    const eggPositionTimer = useRef<number>(0);
    const timeSinceBotMinted = useRef<number>(0);
    const timeSinceReveal = useRef<number>(0);
    const backgroundMaterialRef = useRef() as MutableRefObject<MeshBasicMaterial>;
    const leadTextProps = {
        textAlign: 'center' as const,
        font: garamondNarrowLight,
        fontSize: responsiveValue(0.36, 0.28),
        color: 'white',
        maxWidth: responsiveValue(6, 2.9),
        lineHeight: 1.1,
    };

    const getBotData = useCallback(async () => {
        if (tokenId) {
            try {
                const contract = new ethers.Contract(tokenContractAddress, OnlyBotsToken.abi, provider);
                setBot(OnlyBot.fromJSON(convertShaderToPreset(JSON.parse(await contract.getBotData(tokenId)))));
            } catch (e: any) {
                console.error('error getting bot:', e);
            }
        }
    }, [provider, tokenId]);

    useMount(() => {
        void getBotData();
    });

    const onRevealClick = useCallback(() => {
        setRevealClicked(true);
        explode();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (revealClicked) {
            playMintingSound();
        }
    }, [playMintingSound, revealClicked]);

    useEffect(() => {
        if (revealBot && bot) {
            consolidate(materials, scaleAnchor(bot.anchor));
            playMintedSound();
            stopMintingSound();
        }
    }, [revealBot, playMintedSound, stopMintingSound, bot, consolidate, materials]);

    useLayoutEffect(() => {
        instancedMeshRefs.forEach((ref, i) => {
            const mesh = ref.current;
            if (!mesh) {
                return;
            }

            const material = materials[i];
            if (!material) {
                mesh.count = 0;
                return;
            }

            mesh.count = material.voxels.length;
            applyMaterial(mesh, material.color, material.preset);

            material.voxels.forEach(({ position: [x, y, z] }, index) => {
                TRANSFORMATION_STORAGE.position.set(x, y, z);
                TRANSFORMATION_STORAGE.scale.setScalar(1);
                TRANSFORMATION_STORAGE.updateMatrix();

                mesh.setMatrixAt(index, TRANSFORMATION_STORAGE.matrix);
                mesh.instanceMatrix.needsUpdate = true;
            });
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useFrame((_, delta) => {
        if (revealClicked && !revealBot) {
            timeSinceReveal.current += delta;

            if (timeSinceReveal.current > REVEAL_AFTER_DURATION) {
                setRevealBot(true);
            }
        }

        if (revealBot) {
            timeSinceBotMinted.current += delta;

            if (timeSinceBotMinted.current > REDIRECT_TO_EDITION_DURATION) {
                navigate(`/${tokenId}`);
            }
        }

        if (groupRef.current) {
            if (orientation.current && orientation.current.beta !== null && orientation.current.gamma !== null) {
                groupRef.current.rotation.set(
                    (revealClicked ? 0 : Math.PI / 4) + MathUtils.lerp(0, Math.PI / 8, orientation.current.beta / 180),
                    MathUtils.lerp(revealClicked ? 0 : Math.PI / 4, Math.PI / 3, orientation.current.gamma / 90),
                    0
                );
            } else {
                groupRef.current.rotation.set(
                    (revealClicked ? 0 : Math.PI / 4) + MathUtils.lerp(0, Math.PI / 8, mouse.current.y),
                    MathUtils.lerp(revealClicked ? 0 : Math.PI / 4, Math.PI / 3, mouse.current.x),
                    0
                );
            }
            // lerp position of egg to center
            if (revealClicked && eggPositionTimer.current < TIME_TO_CENTER_EGG) {
                eggPositionTimer.current += delta;
                groupRef.current.position.set(
                    0,
                    clampedLerp(groupRef.current.position.y, 0, eggPositionTimer.current / TIME_TO_CENTER_EGG),
                    0
                );

                // fade out black background
                backgroundMaterialRef.current.opacity = clampedLerp(
                    1,
                    0,
                    eggPositionTimer.current / TIME_TO_CENTER_EGG
                );

                // fade in light intensity
                light1.current.intensity = clampedLerp(
                    0,
                    LIGHT_INTENSITY,
                    eggPositionTimer.current / TIME_TO_CENTER_EGG
                );
                light2.current.intensity = clampedLerp(
                    0,
                    LIGHT_INTENSITY,
                    eggPositionTimer.current / TIME_TO_CENTER_EGG
                );
            }
        }
        if (revealBot) {
            timeSinceBotMinted.current += delta;
            backgroundMaterialRef.current.opacity = 0;

            if (timeSinceBotMinted.current < CONSOLIDATE_TIME) {
                // scale bot
                groupRef.current.scale.set(
                    clampedLerp(0.1, BOT_SCALE, timeSinceBotMinted.current / CONSOLIDATE_TIME),
                    clampedLerp(0.1, BOT_SCALE, timeSinceBotMinted.current / CONSOLIDATE_TIME),
                    clampedLerp(0.1, BOT_SCALE, timeSinceBotMinted.current / CONSOLIDATE_TIME)
                );
            } else if (nameRef.current) {
                nameRef.current.fillOpacity = clampedLerp(
                    0,
                    1,
                    (timeSinceBotMinted.current - CONSOLIDATE_TIME) / NAME_FADE_IN_TIME
                );
            }
        }
    });

    return (
        <>
            <directionalLight ref={light1} intensity={0} castShadow={false} position={[-1, -1, 0.3]} />
            <directionalLight ref={light2} intensity={0} castShadow={false} position={[-10, 10, 10]} />
            <group>
                <mesh position={[0, 0, -1]}>
                    <planeGeometry attach="geometry" args={[500, 500]} />
                    <meshBasicMaterial ref={backgroundMaterialRef} opacity={1} transparent color={bgBlack} />
                </mesh>
                <Environment
                    background={false}
                    files={'https://anima.supply/cdn/onlybots/generator/onlybots-scene-bw-1k.hdr'}
                />
                <Environment
                    background={'only'}
                    files={'https://anima.supply/cdn/onlybots/generator/onlybots-scene-2k.hdr'}
                />
            </group>

            {revealBot && (
                <Text
                    ref={nameRef}
                    position={[0, 2, 1]}
                    textAlign="center"
                    font={garamondNarrowLight}
                    fontSize={0.25}
                    letterSpacing={-0.05}
                    color="white"
                    fillOpacity={0}
                >
                    {bot?.name.toUpperCase()}
                </Text>
            )}
            <group scale={scale}>
                <group ref={groupRef} scale={EGG_SCALE} position={[0, 1.7, 0]}>
                    {instancedMeshRefs.map((ref, i) => (
                        <SelectBloom
                            key={i}
                            enabled={(revealClicked && !revealBot) || (materials[i]?.preset?.bloom ?? false)}
                        >
                            <instancedMesh ref={ref} args={[undefined, undefined, MAX_VOXEL_COUNT]}>
                                <boxGeometry />
                                <meshPhysicalMaterial color={DEFAULT_COLOR} />
                            </instancedMesh>
                        </SelectBloom>
                    ))}
                </group>
            </group>
            {!revealClicked && (
                <Screen>
                    <group position={[0, responsiveValue(0, -0.22), 0]}>
                        <Text position={[0, responsiveValue(-0.2, 0.18), 0]} {...leadTextProps}>
                            Welcome! Your Onlybot is excited to meet you.
                        </Text>
                    </group>
                    <group
                        scale={responsiveValue(1, 0.9)}
                        position={[responsiveValue(-0.9, -0.6), responsiveValue(-1.6, -2.0), 0]}
                    >
                        <PlaystationButton
                            clickableAreaDimensions={[4, 0.7]}
                            position={[-1.0, 0, 0]}
                            icon="square"
                            label="VIEW YOUR ONLYBOT"
                            action={onRevealClick}
                        />
                        <PlaystationButton
                            clickableAreaDimensions={[3, 0.7]}
                            position={[0, responsiveValue(-0.8, -0.9), 0]}
                            icon="x"
                            label="CANCEL"
                            action={cancel}
                        />
                    </group>
                </Screen>
            )}
        </>
    );
};
